import { create } from "zustand";

/**
 * Pilot assistant panel state. Deliberately NOT persisted — the panel
 * open/closed state, active thread and prefill are transient session UI
 * (matches the ui-store convention of keeping transient fields out of
 * partialize; here nothing persists at all).
 */
interface AssistantState {
  open: boolean;
  activeThreadId: string | null;
  /** One-shot composer seed, set by the palette's "Ask Pilot" handoff. */
  prefill: string | null;
  /** The brand the panel last rendered for — thread/prefill state belongs to
   *  it and must not bleed into another brand (the panel survives :brandSlug
   *  changes, and this store survives panel remounts). */
  lastBrandId: string | null;
  openPanel: (opts?: { prefill?: string }) => void;
  /** Palette/polish-plan handoff alias: open the panel with a prefilled prompt. */
  openWithPrompt: (prompt: string) => void;
  closePanel: () => void;
  togglePanel: () => void;
  setActiveThread: (id: string | null) => void;
  /** Returns the prefill and clears it (the panel input consumes it once). */
  consumePrefill: () => string | null;
  /**
   * Records the brand the panel is rendering for. When it differs from the
   * previous non-null brand, drops the active thread + prefill (they belong
   * to the old brand) and returns true so the panel can abort any in-flight
   * stream and clear its local chat state.
   */
  syncBrand: (brandId: string) => boolean;
}

export const useAssistantStore = create<AssistantState>()((set, get) => ({
  open: false,
  activeThreadId: null,
  prefill: null,
  lastBrandId: null,
  openPanel: (opts) =>
    set((s) => ({
      open: true,
      prefill: opts?.prefill !== undefined ? opts.prefill : s.prefill,
    })),
  openWithPrompt: (prompt) => get().openPanel({ prefill: prompt }),
  closePanel: () => set({ open: false }),
  togglePanel: () => set((s) => ({ open: !s.open })),
  setActiveThread: (id) => set({ activeThreadId: id }),
  consumePrefill: () => {
    const prefill = get().prefill;
    if (prefill !== null) set({ prefill: null });
    return prefill;
  },
  syncBrand: (brandId) => {
    const prev = get().lastBrandId;
    if (prev === brandId) return false;
    if (prev === null) {
      // First mount — adopt the brand without disturbing state.
      set({ lastBrandId: brandId });
      return false;
    }
    set({ lastBrandId: brandId, activeThreadId: null, prefill: null });
    return true;
  },
}));
